import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiRefreshCw, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import { contractApi } from '../../../services/contractApi';
import type { ContractTypeDto } from '../../../services/contractApi';

const ContractTypeListPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [contractTypes, setContractTypes] = useState<ContractTypeDto[]>([]);

    useEffect(() => {
        loadContractTypes();
    }, []);

    const loadContractTypes = async () => {
        setLoading(true);
        try {
            const data = await contractApi.getContractTypes();
            setContractTypes(data);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao carregar tipos de contrato');
        } finally {
            setLoading(false);
        }
    };

    const getRolesCount = (ct: ContractTypeDto): number => {
        if (!ct.structures || ct.structures.length === 0) return 0;
        return ct.structures[0]?.roles?.length || 0;
    };

    const getSigningOrderGroups = (ct: ContractTypeDto): number => {
        if (!ct.structures || ct.structures.length === 0) return 0;
        const roles = ct.structures[0]?.roles || [];
        const orders = new Set(roles.map(r => r.signingOrder));
        return orders.size;
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800 p-6">
            {loading && <Loading />}

            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Tipos de Contrato</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configuração de estrutura de assinantes por tipo de contrato</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadContractTypes}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg text-gray-600 dark:text-gray-400"
                            title="Atualizar"
                        >
                            <FiRefreshCw className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/contract-types/new')}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <FiPlus className="w-4 h-4" />
                            Novo Tipo
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                            <tr>
                                <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
                                <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Papéis Configurados</th>
                                <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Grupos de Assinatura</th>
                                <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {contractTypes.map((ct) => (
                                <tr key={ct.name} className="hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{ct.name}</span>
                                            {ct.label && <span className="text-xs text-gray-500 dark:text-gray-400">{ct.label}</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                            {getRolesCount(ct)} papéis
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                                            {getSigningOrderGroups(ct)} grupos
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => navigate(`/contract-types/${ct.name}`)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                                        >
                                            <FiEdit2 className="w-4 h-4" />
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && contractTypes.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Nenhum tipo de contrato encontrado
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ContractTypeListPage;
